"use client";

import { Button } from "@/components/ui/button-tw";

import {
  ColumnDef,
  getCoreRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Upload, X, Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

import TablePagination from "@/components/table-pagination";
import MainTable from "@/components/main-table";
import { FileUploader } from "react-drag-drop-files";
import {
  BulkInvoiceUploadEntities,
  BulkInvoiceUploadEntity,
  BulkInvoiceUploadStatus,
  DownloadFileType,
} from "@/queries/invoice/types";
import { invoiceQueries, invoiceQueryKeys } from "@/queries/invoice";
import { useAuth } from "@/providers/auth";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { capitalizeFirstLetter } from "@/utils/string-manipulation";
import { cn } from "@/utils/classnames";

const useBulkImportTable = (userId: string) => {
  const [bulkUploadId, setBulkUploadId] = useState<string | null>(null);
  const [downloadingType, setDownloadingType] =
    useState<DownloadFileType | null>(null);

  const { refetch: refetchDownload } =
    invoiceQueries.bulkUpload.download.useQuery(
      userId,
      downloadingType || "template",
      bulkUploadId || "",
      {
        enabled: false,
        onSuccess: (url) => {
          const link = document.createElement("a");
          link.href = url;
          link.download = `bulk-upload-${downloadingType || "fail"}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setBulkUploadId(null);
          setDownloadingType(null);
        },
      }
    );

  const handleDownload = (type: DownloadFileType, bulkUploadId: string) => {
    setBulkUploadId(bulkUploadId);
    setDownloadingType(type);
  };

  useEffect(() => {
    if ((bulkUploadId && downloadingType) || downloadingType === "template") {
      refetchDownload();
    }
  }, [bulkUploadId, downloadingType]);

  const columns: ColumnDef<BulkInvoiceUploadEntity>[] = [
    {
      header: "Date",
      accessorFn: (row) => dayjs(row.created_at).format("MM/DD/YYYY"),
    },
    {
      id: "name",
      header: "Created By",
      accessorFn: (row) => row.created_by_name,
    },
    {
      id: "originalFile",
      header: "Original File",
      accessorFn: (row) => row.original_file_name,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() =>
            handleDownload("original", row.original.bulk_upload_id)
          }
          className="text-blue-700"
          style={{ textDecoration: "underline" }}
        >
          {bulkUploadId === row.original.bulk_upload_id &&
          downloadingType === "original"
            ? "Downloading..."
            : row.getValue("originalFile") || "Click Here"}
        </button>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "upload_status",
      cell: ({ row }) => (
        <BulkUploadStatusBadge status={row.getValue("status")} />
      ),
      enableSorting: false,
    },
    {
      id: "numberOfRecords",
      header: "Number of Records",
      accessorKey: "total_records",
    },
    {
      id: "failedRows",
      header: "Failed Rows",
      accessorKey: "failed_records",
    },
    {
      id: "failedFile",
      header: "Failed File",
      accessorFn: (row) => row.failed_file_name,
      cell: ({ row }) =>
        row.original.failed_file_name &&
        row.original.failed_file_name !== "none" ? (
          <button
            type="button"
            onClick={() =>
              handleDownload("failed", row.original.bulk_upload_id)
            }
            className="text-blue-700"
            style={{ textDecoration: "underline" }}
          >
            {bulkUploadId === row.original.bulk_upload_id &&
            downloadingType === "failed"
              ? "Downloading..."
              : row.getValue("failedFile") || "Click Here"}
          </button>
        ) : null,
    },
  ];

  return { columns, handleDownload };
};

const fallbackData: BulkInvoiceUploadEntities = [];

export default function BulkImportTable() {
  const queryClient = useQueryClient();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const uploadList = invoiceQueries.bulkUpload.list.useQuery(userId);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [bulkUploadId, setBulkUploadId] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    | "idle"
    | "uploading"
    | "pending"
    | "processing"
    | "success"
    | "failed"
    | "rejected"
    | "error"
  >("idle");
  const tableRef = useRef(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { columns, handleDownload } = useBulkImportTable(userId!);

  const handleChangeCSVFile = async (file: File[]) => {
    setFile(file[0]);
  };

  // Upload logic
  const { mutate: uploadFile } = invoiceQueries.bulkUpload.upload.useMutation({
    onSuccess: (response) => {
      setUploadStatus(response.upload_status); // Should be "pending"
      setBulkUploadId(response.bulk_upload_id);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.data.detail) {
          toast.error(
            error.response?.data.detail ||
              "Ops something went wrong. We are working to fix it.",
            {
              autoClose: 10000,
            }
          );
        }
      }
      setUploadStatus("error");
    },
  });

  const canPoll =
    !!bulkUploadId &&
    (uploadStatus === "processing" || uploadStatus === "pending");
  const { data: bulkUploadEntity } = invoiceQueries.bulkUpload.entity.useQuery(
    userId || "",
    bulkUploadId || "",
    {
      enabled: canPoll,
      refetchInterval: canPoll ? 5000 : false,
    }
  );

  useEffect(() => {
    const uploadStatus = bulkUploadEntity?.upload_status;
    if (!uploadStatus) return;

    setUploadStatus(uploadStatus);

    if (["success", "failed", "rejected", "error"].includes(uploadStatus)) {
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.all,
      });

      if (uploadStatus === "success") {
        toast.success("Invoices processed successfully!", {
          autoClose: 5000,
        });
      }

      if (uploadStatus === "failed") {
        toast.error("Invoices processing failed.", { autoClose: 5000 });
      }

      if (uploadStatus === "rejected") {
        toast.error("Invoices processing rejected!", { autoClose: 5000 });
      }

      if (uploadStatus === "error") {
        toast.error("An error occurred while processing invoices.", {
          autoClose: 5000,
        });
      }

      setBulkUploadOpen(false);
      setFile(null);
      setUploadStatus("idle");
    }
  }, [bulkUploadEntity?.upload_status]);

  const handleUpload = () => {
    if (!file || !userId) return;
    setUploadStatus("uploading");
    uploadFile({ userId, file });
  };

  const table = useReactTable({
    data: uploadList.data || fallbackData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    // onColumnFiltersChange: setColumnFilters,
    // onColumnVisibilityChange: setColumnVisibility,
    // getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      pagination,
      sorting,
    },
  });

  return (
    <div className="space-y-6 mb-96">
      {/* Filters */}
      <div className=" sm:flex sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-gray-900">Activity</h3>
        <div className="mt-3 flex flex-col md:flex-row sm:ml-4 sm:mt-0 gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleDownload("template", "")}
          >
            <Download
              className="-ms-1 me-2 "
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
            Download Template
          </Button>
          <Button
            className="w-full bg-green-600 hover:bg-green-600/80 text-white lg:w-48"
            onClick={() => setBulkUploadOpen(true)}
            disabled={bulkUploadOpen}
          >
            <Upload
              className="-ms-1 me-2 "
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
            New Bulk Upload
          </Button>
        </div>
      </div>

      {bulkUploadOpen && (
        <div className="relative divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow ">
          <div
            className="px-4 py-4 sm:px-5 transition-all duration-1000 ease-in-out transform opacity-0 translate-y-4 data-[visible=true]:opacity-100 data-[visible=true]:translate-y-0"
            data-visible={bulkUploadOpen}
          >
            <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div className="ml-4 mt-2">
                <h3 className="text-base font-semibold text-gray-900">
                  Bulk Upload
                </h3>
              </div>
              <div className="ml-4 mt-2 shrink-0">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    setFile(null);
                    setBulkUploadOpen(false);
                    setUploadStatus("idle");
                  }}
                >
                  <X />
                </Button>
              </div>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <dl className="">
              <div className=" sm:flex">
                <dt className="font-medium text-gray-900 sm:w-96 sm:flex-none sm:pr-6">
                  <div>
                    Automatically create draft invoices from a CSV file.
                  </div>
                  <div className="text-sm font-normal text-gray-500">
                    Use our template to ensure correct formatting. After
                    submission, processing happens in the background. Check the
                    activity table to see if your upload succeeded and download
                    a file with any failed rows.
                  </div>
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="w-full">
                    <FileUploader
                      multiple={true}
                      handleChange={handleChangeCSVFile}
                      name="file"
                      types={["CSV"]}
                      children={
                        <div className="flex items-center justify-center w-full">
                          <label
                            htmlFor="dropzone-file"
                            className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 "
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <svg
                                className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 16"
                              >
                                <path
                                  stroke="currentColor"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                />
                              </svg>
                              {file ? (
                                <p className="mb-2 text-lg text-gray-500 dark:text-gray-400">
                                  {file.name}
                                </p>
                              ) : (
                                <>
                                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">
                                      Click to upload
                                    </span>{" "}
                                    or drag and drop
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    CSV file
                                  </p>
                                </>
                              )}
                            </div>
                            <input
                              id="dropzone-file"
                              type="file"
                              className="hidden"
                            />
                          </label>
                        </div>
                      }
                    />
                  </div>
                </dd>
              </div>
            </dl>
          </div>
          <div className="px-4 py-4 sm:px-6 flex justify-end">
            <Button
              className="px-10"
              onClick={handleUpload}
              disabled={
                !file ||
                uploadStatus === "uploading" ||
                uploadStatus === "pending" ||
                uploadStatus === "processing"
              }
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload & Create Invoices
            </Button>
          </div>

          {/* Overlay */}
          {uploadStatus !== "idle" && uploadStatus !== "error" && (
            <div className="absolute inset-0 bg-gray-500/50 flex items-center justify-center transition-opacity duration-300 ease-in-out">
              <div
                className={`
                text-center text-white p-4 rounded-lg bg-gray-800/80
                transition-transform duration-300 ease-in-out transform
                scale-95 data-[status=success]:scale-100 data-[status=failed]:scale-100 data-[status=rejected]:scale-100 data-[status=error]:scale-100
                relative overflow-hidden
                ${
                  uploadStatus === "uploading" ||
                  uploadStatus === "pending" ||
                  uploadStatus === "processing"
                    ? "animate-loading-bg"
                    : ""
                }
              `}
                data-status={uploadStatus}
              >
                {uploadStatus === "uploading" && (
                  <p className="text-lg relative z-10">Uploading file...</p>
                )}
                {uploadStatus === "pending" && (
                  <p className="text-lg relative z-10">Pending scan...</p>
                )}
                {uploadStatus === "processing" && (
                  <p className="text-lg relative z-10">
                    Processing invoices...
                  </p>
                )}
                {uploadStatus === "success" && (
                  <p className="text-lg text-green-300 relative z-10">
                    Upload and processing complete!
                  </p>
                )}
                {uploadStatus === "failed" && (
                  <p className="text-lg text-red-300 relative z-10">
                    Some invoices failed to process.
                  </p>
                )}
                {uploadStatus === "rejected" && (
                  <p className="text-lg text-red-300 relative z-10">
                    File rejected due to scan failure.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <MainTable ref={tableRef} table={table} isLoading={false} />
      <TablePagination table={table} />
    </div>
  );
}

type BulkUploadStatusBadgeProps = {
  status: BulkInvoiceUploadStatus;
};

export function BulkUploadStatusBadge({ status }: BulkUploadStatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20",
        {
          "text-red-700 ring-red-600/20 bg-red-50":
            status === "error" || status === "rejected" || status === "failed",
          "text-yellow-700 ring-yellow-600/20 bg-yellow-50":
            status === "processing" || status === "pending",
          "text-green-700 ring-green-600/20 bg-green-50": status === "success",
        }
      )}
    >
      <span>{capitalizeFirstLetter(status)}</span>
    </span>
  );
}
